import type { FileStatusItemSchema } from "@/hooks/api"
import type { KnowledgeNode } from "../types"

export interface PendingKnowledgeBaseNode {
  workspaceId: string
  kbId: string
}

export interface WorkspaceStatusPollBatch {
  workspaceId: string
  nodeIds: string[]
}

export type KnowledgeBaseNodeStatusIndex = Map<
  string,
  string | null | undefined
>

export const WORKSPACE_FILE_STATUS_BATCH_SIZE = 100
export const KNOWLEDGE_BASE_POLLING_INTERVALS_MS = {
  fast: 1500,
  normal: 5000,
  slow: 15000,
} as const

export type KnowledgeBasePollingStage =
  keyof typeof KNOWLEDGE_BASE_POLLING_INTERVALS_MS
export type KnowledgeBasePollingOutcome =
  | "progress"
  | "partial_progress"
  | "no_progress"
  | "error"

export interface KnowledgeBasePollingSchedulerState {
  stage: KnowledgeBasePollingStage
  noProgressCount: number
}

export function hasPendingKnowledgeBaseStatus(status?: string | null): boolean {
  return status === "PENDING" || status === "INDEXING"
}

export function createKnowledgeBasePollingSchedulerState(): KnowledgeBasePollingSchedulerState {
  return {
    stage: "fast",
    noProgressCount: 0,
  }
}

export function getKnowledgeBasePollingDelayMs(
  state: KnowledgeBasePollingSchedulerState
): number {
  return KNOWLEDGE_BASE_POLLING_INTERVALS_MS[state.stage]
}

export function resolveKnowledgeBasePollingOutcome(flags: {
  hadProgress: boolean
  hadError: boolean
}): KnowledgeBasePollingOutcome {
  if (flags.hadProgress && flags.hadError) return "partial_progress"
  if (flags.hadProgress) return "progress"
  if (flags.hadError) return "error"
  return "no_progress"
}

export function advanceKnowledgeBasePollingScheduler(
  state: KnowledgeBasePollingSchedulerState,
  outcome: KnowledgeBasePollingOutcome
): KnowledgeBasePollingSchedulerState {
  if (outcome === "progress") {
    return createKnowledgeBasePollingSchedulerState()
  }

  if (outcome === "partial_progress") {
    return {
      stage: "normal",
      noProgressCount: 1,
    }
  }

  const noProgressCount = state.noProgressCount + 1

  if (noProgressCount >= 2) {
    return {
      stage: "slow",
      noProgressCount,
    }
  }

  return {
    stage: "normal",
    noProgressCount,
  }
}

export function collectKnowledgeBaseNodeIds(node: KnowledgeNode): string[] {
  const nodeIds = [node.id]
  for (const child of node.children || []) {
    nodeIds.push(...collectKnowledgeBaseNodeIds(child))
  }
  return nodeIds
}

export function buildWorkspaceStatusPollBatches(
  pendingNodeMap: Map<string, PendingKnowledgeBaseNode>,
  chunkSize = WORKSPACE_FILE_STATUS_BATCH_SIZE
): WorkspaceStatusPollBatch[] {
  if (chunkSize <= 0) {
    throw new Error("chunkSize must be greater than 0")
  }

  const byWorkspace =
    collectPendingKnowledgeBaseNodeIdsByWorkspace(pendingNodeMap)

  const batches: WorkspaceStatusPollBatch[] = []
  for (const [workspaceId, nodeIds] of byWorkspace.entries()) {
    for (let index = 0; index < nodeIds.length; index += chunkSize) {
      batches.push({
        workspaceId,
        nodeIds: nodeIds.slice(index, index + chunkSize),
      })
    }
  }

  return batches
}

export function collectPendingKnowledgeBaseNodeIdsByWorkspace(
  pendingNodeMap: Map<string, PendingKnowledgeBaseNode>
): Map<string, string[]> {
  const byWorkspace = new Map<string, string[]>()

  for (const [nodeId, info] of pendingNodeMap.entries()) {
    const nodeIds = byWorkspace.get(info.workspaceId)
    if (nodeIds) {
      nodeIds.push(nodeId)
      continue
    }
    byWorkspace.set(info.workspaceId, [nodeId])
  }

  return byWorkspace
}

export function collectPendingKnowledgeBaseNodeIdsForWorkspace(
  pendingNodeMap: Map<string, PendingKnowledgeBaseNode>,
  workspaceId: string
): string[] {
  return (
    collectPendingKnowledgeBaseNodeIdsByWorkspace(pendingNodeMap).get(
      workspaceId
    ) || []
  )
}

export function collectResolvedPendingKnowledgeBaseNodeIds(
  requestedNodeIds: string[],
  items: FileStatusItemSchema[]
): string[] {
  const resolvedNodeIds = new Set<string>()
  const returnedNodeIds = new Set(items.map((item) => item.node_id))

  for (const nodeId of requestedNodeIds) {
    if (!returnedNodeIds.has(nodeId)) {
      resolvedNodeIds.add(nodeId)
    }
  }

  for (const item of items) {
    if (!hasPendingKnowledgeBaseStatus(item.status)) {
      resolvedNodeIds.add(item.node_id)
    }
  }

  return Array.from(resolvedNodeIds)
}

export function removePendingKnowledgeBaseNodes(
  pendingNodeMap: Map<string, PendingKnowledgeBaseNode>,
  nodeIds: string[]
): Map<string, PendingKnowledgeBaseNode> {
  if (nodeIds.length === 0) return pendingNodeMap

  let next: Map<string, PendingKnowledgeBaseNode> | null = null

  for (const nodeId of nodeIds) {
    if (!(next || pendingNodeMap).has(nodeId)) continue
    if (!next) {
      next = new Map(pendingNodeMap)
    }
    next.delete(nodeId)
  }

  return next ?? pendingNodeMap
}

export function buildKnowledgeBaseNodeStatusIndex(
  root: KnowledgeNode
): KnowledgeBaseNodeStatusIndex {
  const statuses: KnowledgeBaseNodeStatusIndex = new Map()

  const visit = (node: KnowledgeNode) => {
    statuses.set(node.id, node.status)

    for (const child of node.children || []) {
      visit(child)
    }
  }

  visit(root)

  return statuses
}

export function resolveKnowledgeBasePollingChunkProgress({
  baseStatuses,
  overlayStatuses,
  requestedNodeIds,
  items,
}: {
  baseStatuses: ReadonlyMap<string, string | null | undefined>
  overlayStatuses: Map<string, string | null | undefined>
  requestedNodeIds: string[]
  items: FileStatusItemSchema[]
}): boolean {
  let hadProgress = false

  if (
    collectResolvedPendingKnowledgeBaseNodeIds(requestedNodeIds, items).length >
    0
  ) {
    hadProgress = true
  }

  if (items.length === 0) return hadProgress

  for (const item of items) {
    const hasKnownStatus =
      overlayStatuses.has(item.node_id) || baseStatuses.has(item.node_id)
    if (!hasKnownStatus) {
      continue
    }

    const previousStatus = overlayStatuses.has(item.node_id)
      ? overlayStatuses.get(item.node_id)
      : baseStatuses.get(item.node_id)

    if (previousStatus !== item.status) {
      hadProgress = true
    }

    overlayStatuses.set(item.node_id, item.status)
  }

  return hadProgress
}

export function pruneResolvedPendingKnowledgeBaseNodes(
  pendingNodeMap: Map<string, PendingKnowledgeBaseNode>,
  requestedNodeIds: string[],
  items: FileStatusItemSchema[]
): Map<string, PendingKnowledgeBaseNode> {
  return removePendingKnowledgeBaseNodes(
    pendingNodeMap,
    collectResolvedPendingKnowledgeBaseNodeIds(requestedNodeIds, items)
  )
}

export function applyKnowledgeBaseFileStatusUpdates(
  root: KnowledgeNode,
  items: FileStatusItemSchema[]
): KnowledgeNode {
  if (items.length === 0) return root

  const itemsByNodeId = new Map(items.map((item) => [item.node_id, item]))

  const visit = (node: KnowledgeNode): KnowledgeNode => {
    const item = itemsByNodeId.get(node.id)
    const children = node.children
    let nextChildren = children
    let childrenChanged = false

    if (children && children.length > 0) {
      nextChildren = children.map((child) => {
        const nextChild = visit(child)
        if (nextChild !== child) {
          childrenChanged = true
        }
        return nextChild
      })
    }

    const nodeChanged =
      !!item &&
      (node.status !== item.status ||
        node.processing_state !== item.processing_state ||
        node.last_processing_error !== item.last_processing_error ||
        node.updated_at !== item.updated_at)

    if (!nodeChanged && !childrenChanged) {
      return node
    }

    return {
      ...node,
      ...(item
        ? {
            status: item.status,
            processing_state: item.processing_state,
            last_processing_error: item.last_processing_error,
            updated_at: item.updated_at,
          }
        : {}),
      ...(childrenChanged ? { children: nextChildren } : {}),
    }
  }

  return visit(root)
}
